import {
  createContext,
  useContext,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { Swatch } from '../../api';
import type { ProductCardBasicProps } from './product-card.type';
import {
  DatasetIdEnumFroChild,
  type BuriedPointBehavior,
  type BuriedPointType,
  type TagAttributes,
} from '../../hooks';

type Dataset = Omit<TagAttributes, 'id'>;

export type ProductCardContextState = ProductCardBasicProps & {
  swatches: Swatch[];
  currentSwatch: Swatch;
  isVertical: boolean;
  isHorizontal: boolean;
  setCurrentSwatch: Dispatch<SetStateAction<Swatch>>;
  generateDataset: (id?: BuriedPointBehavior) => Dataset | null;
};

export const ProductCardContext = createContext<ProductCardContextState | null>(
  null
);

export type ProductCardContextProviderProps = ProductCardBasicProps & {
  children: ReactNode;
};

export function ProductCardContextProvider({
  children,
  ...props
}: ProductCardContextProviderProps) {
  const swatches = props.product.swatches.filter(
    ({ colorImage, spec }) => colorImage || spec
  );
  const isVertical = props.mode === 'vertical';
  const isHorizontal = props.mode === 'horizontal';

  const [currentSwatch, setCurrentSwatch] = useState(() => {
    const { sku, swatches } = props.product;
    return swatches.find((item) => item.sku === sku) as Swatch;
  });

  const generateDataset: ProductCardContextState['generateDataset'] = (id) => {
    if (!props.catalog) return null;
    const dataset: Partial<Dataset> = {
      'data-id-catalog': props.catalog,
      'data-id-item': props.product.sku,
    };
    if (!id) {
      const type = props.catalog.replace(/[0-9]/g, '') as BuriedPointType;
      dataset['data-id-id'] = DatasetIdEnumFroChild[type];
    } else {
      dataset['data-id-id'] = id;
    }
    return dataset as Dataset;
  };

  return (
    <ProductCardContext.Provider
      value={{
        ...props,
        swatches,
        currentSwatch,
        isVertical,
        isHorizontal,
        setCurrentSwatch,
        generateDataset,
      }}
    >
      {children}
    </ProductCardContext.Provider>
  );
}

export function useProductCardContext() {
  const context = useContext(ProductCardContext);
  if (!context) {
    throw new Error(
      'useProductCardContext must be used within a ProductCardContextProvider'
    );
  }
  return context;
}
